import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, FileText, ShieldCheck } from "lucide-react";

import { apiClient, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Logo } from "@/components/Logo";

/**
 * Subscriber Responsibilities / FCRA Acknowledgment — Phase G (2026-02).
 *
 * Mandatory gate that appears after login for property-manager users the
 * first time they encounter a new acknowledgment version. All copy, doc
 * URLs, and the version identifier are fetched from
 * `GET /api/subscriber-ack/current` — never hardcoded here.
 *
 * On successful submission the backend records an immutable acknowledgment
 * (auditable per-user), stamps a fast-path pointer on the user record, and
 * this page refreshes AuthContext + navigates to `/dashboard`.
 */
export default function SubscriberAcknowledgmentPage() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();

  const [content, setContent] = useState(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await apiClient.get("/subscriber-ack/current");
        if (!alive) return;
        setContent(data);
        // If the backend says already accepted (e.g. duplicate tab, race),
        // bounce straight to the dashboard.
        if (data.already_accepted) {
          await refresh();
          navigate("/dashboard", { replace: true });
        }
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Failed to load acknowledgment");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const canSubmit = useMemo(() => checked && !submitting && !!content, [checked, submitting, content]);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await apiClient.post("/subscriber-ack/accept");
      await refresh();
      toast.success("Acknowledgment recorded");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not record acknowledgment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#0A192F]" data-testid="subscriber-ack-page">
      {/* Slim top bar — deliberately minimal. No primary nav; no dashboard link. */}
      <header className="border-b border-black/5 bg-white/80 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4 text-sm">
            {user?.email && (
              <span className="hidden sm:inline text-[#4A5568]" data-testid="ack-user-email">
                {user.email}
              </span>
            )}
            <button
              onClick={logout}
              className="text-[#4A5568] hover:text-[#0A192F] underline underline-offset-4"
              data-testid="ack-signout-btn"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 md:py-14">
        {loading ? (
          <div className="text-[#4A5568] font-overline">Loading…</div>
        ) : error ? (
          <Card className="p-6 border-red-200 bg-red-50 text-red-800">{error}</Card>
        ) : content ? (
          <>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#4A5568] mb-3 flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5" />
              Subscriber Responsibilities · v{content.version}
            </div>
            <h1 className="font-serif text-4xl md:text-5xl leading-tight" data-testid="ack-title">
              {content.title}
            </h1>
            <p className="mt-5 text-[15px] leading-relaxed text-[#4A5568] max-w-3xl">
              {content.intro}
            </p>

            <Card className="mt-8 p-6 md:p-8 border-black/5 shadow-sm" data-testid="ack-documents-card">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#4A5568]">
                {content.downloads_heading}
              </div>
              <ul className="mt-4 divide-y divide-black/5">
                {content.documents.map((doc, i) => (
                  <li key={doc.url}>
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between py-3 group hover:bg-black/[0.02] px-2 -mx-2 rounded transition-colors"
                      data-testid={`ack-doc-link-${i}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="h-4 w-4 text-[#4A5568] shrink-0" />
                        <span className="text-[14px] text-[#0A192F] truncate">{doc.label}</span>
                      </div>
                      <ExternalLink className="h-3.5 w-3.5 text-[#4A5568] opacity-60 group-hover:opacity-100 shrink-0 ml-3" />
                    </a>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="mt-6 p-6 md:p-8 border-black/5 shadow-sm" data-testid="ack-certifications-card">
              <p className="text-[14px] leading-relaxed text-[#0A192F]">
                {content.certifications_preface}
              </p>
              <ul className="mt-5 space-y-3">
                {content.certifications.map((cert, i) => (
                  <li key={i} className="flex gap-3 text-[14px] leading-relaxed text-[#0A192F]"
                      data-testid={`ack-cert-${i}`}>
                    <span className="text-[#4A5568] shrink-0 mt-1">•</span>
                    <span>{cert}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="mt-6 p-6 md:p-8 border-black/5 shadow-sm bg-white" data-testid="ack-consent-card">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <Checkbox
                  checked={checked}
                  onCheckedChange={(v) => setChecked(Boolean(v))}
                  className="mt-1"
                  data-testid="ack-checkbox"
                />
                <span className="text-[14px] leading-relaxed text-[#0A192F]">
                  {content.checkbox_label}
                  <span className="block text-[12px] text-[#4A5568] mt-1">
                    I certify that I have read, understand, and agree to the above requirements.
                  </span>
                </span>
              </label>

              <div className="mt-6 flex flex-col sm:flex-row-reverse gap-3 sm:items-center sm:justify-between">
                <Button
                  onClick={submit}
                  disabled={!canSubmit}
                  className="min-w-[160px]"
                  data-testid="ack-continue-btn"
                >
                  {submitting ? "Submitting…" : (content.continue_label || "Continue")}
                </Button>
                <div className="text-[11px] text-[#4A5568]">
                  Version <span className="font-mono">{content.version}</span> · Your acceptance is recorded and auditable.
                </div>
              </div>
            </Card>
          </>
        ) : null}
      </main>
    </div>
  );
}
